import { logger } from "@artis/logger";
import { useEffect, useState } from "react";

type Protocol = "http/1.1" | "h2" | "h3" | string;

function isHttp1(proto?: Protocol): boolean {
  return proto === "http/1.1";
}

const lastLog = { time: 0 };

export function useConnectionIsHttp1(): boolean | null {
  const [isHttp1State, setIsHttp1State] = useState<boolean | null>(null);

  useEffect(() => {
    function checkNavigation(): boolean | null {
      const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
      if (!navEntries.length) return null;

      const proto = navEntries[0].nextHopProtocol as Protocol | undefined;
      if (!proto) return null;

      return isHttp1(proto);
    }

    function checkResources(): boolean | null {
      const resources = performance.getEntriesByType("resource") as PerformanceResourceTiming[];

      if (!resources.length) return null;

      let http1Count = 0;
      let knownCount = 0;

      for (const r of resources) {
        const proto = r.nextHopProtocol as Protocol | undefined;
        if (!proto) continue;

        knownCount++;
        if (isHttp1(proto)) http1Count++;
      }

      if (knownCount === 0) return null;

      // If majority are HTTP/1.1, assume connection is HTTP/1.1
      return http1Count / knownCount > 0.5;
    }

    function evaluate() {
      const navResult = checkNavigation();
      if (navResult !== null) {
        setIsHttp1State(navResult);
        return;
      }

      const resResult = checkResources();
      if (resResult !== null) {
        setIsHttp1State(resResult);
      }
    }

    evaluate();

    // Observe new resource entries (for SPAs / lazy loading).
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries() as PerformanceResourceTiming[];

      let http1Seen = false;
      let otherSeen = false;

      for (const entry of entries) {
        const proto = entry.nextHopProtocol as Protocol | undefined;
        if (!proto) continue;

        if (isHttp1(proto)) http1Seen = true;
        else otherSeen = true;
      }

      if (http1Seen && !otherSeen) {
        setIsHttp1State(true);
      } else if (otherSeen) {
        setIsHttp1State(false);
      }
    });

    try {
      observer.observe({ type: "resource", buffered: true });
    } catch {
      // Some browsers don't support this, just ignore if it fails.
    }

    return () => observer.disconnect();
  }, []);

  // Log every 2 minutes if the connection is HTTP/1.1, to help identify if this is a common issue for users. Run first log immediately.
  useEffect(() => {
    if (isHttp1State) {
      const now = Date.now();
      if (now - lastLog.time > 120_000 || lastLog.time === 0) {
        logger.warn("HTTP1Error - User is on HTTP/1.1 connection, which may cause slow loading of charts", {
          timestamp: new Date().toISOString(),
        });
        lastLog.time = now;
      }
    }
  }, [isHttp1State]);

  return isHttp1State;
}
